import React from 'react';
import {
  Box,
  HStack,
  Text,
  VStack,
  Button,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  Badge,
} from '@chakra-ui/react';
import { FaFilter, FaTimes } from 'react-icons/fa';

interface ValueRangeFilterProps {
  minValue: number | null;
  maxValue: number | null;
  onMinValueChange: (value: number | null) => void;
  onMaxValueChange: (value: number | null) => void;
  onClear: () => void;
  isActive: boolean;
}

export const ValueRangeFilter: React.FC<ValueRangeFilterProps> = ({
  minValue,
  maxValue,
  onMinValueChange,
  onMaxValueChange,
  onClear,
  isActive,
}) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const handleMinValueChange = (valueAsString: string, valueAsNumber: number) => {
    if (valueAsString === '' || isNaN(valueAsNumber)) {
      onMinValueChange(null);
    } else {
      onMinValueChange(valueAsNumber);
    }
  };

  const handleMaxValueChange = (valueAsString: string, valueAsNumber: number) => {
    if (valueAsString === '' || isNaN(valueAsNumber)) {
      onMaxValueChange(null);
    } else {
      onMaxValueChange(valueAsNumber);
    }
  };

  const getFilterText = () => {
    if (minValue !== null && maxValue !== null) {
      return `${formatCurrency(minValue)} - ${formatCurrency(maxValue)}`;
    } else if (minValue !== null) {
      return `≥ ${formatCurrency(minValue)}`;
    } else if (maxValue !== null) {
      return `≤ ${formatCurrency(maxValue)}`;
    }
    return 'Filtrar por valor';
  };

  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>
        <Button
          size="sm"
          variant={isActive ? 'solid' : 'outline'}
          colorScheme={isActive ? 'blue' : 'gray'}
          leftIcon={<Icon as={FaFilter} />}
          rightIcon={isActive ? <Icon as={FaTimes} /> : undefined}
          onClick={isActive ? onClear : undefined}
          _hover={{
            transform: 'translateY(-1px)',
            boxShadow: 'md',
          }}
          transition="all 0.2s ease"
        >
          <HStack spacing={2}>
            <Text fontSize="xs" fontWeight="medium">
              {getFilterText()}
            </Text>
            {isActive && (
              <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                Ativo
              </Badge>
            )}
          </HStack>
        </Button>
      </PopoverTrigger>
      <PopoverContent width="400px" bg="white" border="1px solid" borderColor="gray.200" boxShadow="lg">
        <PopoverArrow />
        <PopoverBody p={4}>
          <VStack spacing={4} align="stretch">
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                Filtro por Valor
              </Text>
              {isActive && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorScheme="red"
                  onClick={onClear}
                  leftIcon={<Icon as={FaTimes} />}
                >
                  Limpar
                </Button>
              )}
            </HStack>

            <HStack spacing={3} align="end">
              <VStack spacing={1} align="stretch" flex={1}>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">
                  Valor Mínimo
                </Text>
                <NumberInput
                  value={minValue || ''}
                  onChange={handleMinValueChange}
                  precision={2}
                  step={0.01}
                  min={0}
                  size="sm"
                >
                  <NumberInputField
                    placeholder="R$ 0,00"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px blue.500',
                    }}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </VStack>

              <VStack spacing={1} align="stretch" flex={1}>
                <Text fontSize="xs" color="gray.600" fontWeight="medium">
                  Valor Máximo
                </Text>
                <NumberInput
                  value={maxValue || ''}
                  onChange={handleMaxValueChange}
                  precision={2}
                  step={0.01}
                  min={minValue || 0}
                  size="sm"
                >
                  <NumberInputField
                    placeholder="R$ 999.999,99"
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{
                      borderColor: 'blue.500',
                      boxShadow: '0 0 0 1px blue.500',
                    }}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </VStack>
            </HStack>

            {isActive && (
              <Box fontSize="xs" color="blue.600" bg="blue.50" p={2} borderRadius="sm" border="1px solid" borderColor="blue.200">
                <Text fontWeight="medium">
                  Filtro ativo: {minValue ? formatCurrency(minValue) : 'Qualquer'} até{' '}
                  {maxValue ? formatCurrency(maxValue) : 'Qualquer'}
                </Text>
              </Box>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
